import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <div className="glass-header px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="p-1 -ml-1" data-testid="terms-back-btn">
          <ArrowLeft className="w-5 h-5 text-[#111B21]" />
        </button>
        <h1 className="text-[17px] font-semibold text-[#111B21]">Terms of Service</h1>
      </div>
      <div className="max-w-2xl mx-auto px-5 py-6 text-[#111B21]">
        <p className="text-[13px] text-[#667781] mb-6">Last updated: February 2026</p>

        <Section title="1. Acceptance of Terms">
          By accessing or using CallChat AI ("CallChat", "the Service"), you agree to be bound by these
          Terms of Service. If you do not agree to these terms, please do not use CallChat.
        </Section>

        <Section title="2. Description of Service">
          CallChat is a communication platform that provides messaging, voice and video calling, group
          meetings, AI-powered video generation, and related services. The Service is provided by
          The Career Pathway (thecareerpathway.in).
        </Section>

        <Section title="3. Account Registration">
          <BulletList items={[
            'You must provide accurate and complete information when creating an account.',
            'You are responsible for maintaining the security of your account credentials.',
            'You must be at least 13 years old to use CallChat.',
            'One person may not maintain more than one account.',
          ]} />
        </Section>

        <Section title="4. Acceptable Use">
          You agree NOT to use CallChat to:
          <BulletList items={[
            'Send spam, unsolicited messages, or engage in harassment.',
            'Distribute illegal, harmful, threatening, or objectionable content.',
            'Impersonate any person or entity.',
            'Attempt to gain unauthorized access to other accounts or systems.',
            'Use automated systems, bots, or scrapers without our permission.',
            'Interfere with or disrupt the Service or its infrastructure.',
            'Violate any applicable local, state, national, or international law.',
          ]} />
        </Section>

        <Section title="5. Content & Intellectual Property">
          You retain ownership of content you create and share through CallChat. By using the Service,
          you grant us a limited license to transmit, store, and deliver your content as necessary to
          provide the Service. AI-generated content created through our AI Video feature is subject to
          the usage terms of our AI providers.
        </Section>

        <Section title="6. Payments & Credits">
          <BulletList items={[
            'CallChat uses a credit-based system for premium features like AI Video generation.',
            'All payments are processed through Razorpay and are subject to their terms.',
            'Credits are non-refundable once purchased unless required by applicable law.',
            'We reserve the right to modify pricing and credit packages at any time.',
          ]} />
        </Section>

        <Section title="7. Meetings & Calls">
          <BulletList items={[
            'Meeting hosts are responsible for the content and conduct within their meetings.',
            'Meetings support up to 50 participants.',
            'We do not record meetings or calls. Any recording is the responsibility of participants.',
            'You agree to obtain consent from all participants before recording any call or meeting.',
          ]} />
        </Section>

        <Section title="8. Privacy">
          Your use of CallChat is also governed by our <a href="/privacy" className="text-[#00A884] underline">Privacy Policy</a>,
          which explains how we collect, use, and protect your information.
        </Section>

        <Section title="9. Termination">
          We reserve the right to suspend or terminate your account if you violate these Terms. You may
          delete your account at any time through the Settings menu. Upon termination, your right to use
          the Service will immediately cease.
        </Section>

        <Section title="10. Disclaimers">
          CallChat is provided "as is" and "as available" without warranties of any kind, either express
          or implied. We do not guarantee that the Service will be uninterrupted, secure, or error-free.
          We are not responsible for any content shared by users through the platform.
        </Section>

        <Section title="11. Limitation of Liability">
          To the maximum extent permitted by law, CallChat and its operators shall not be liable for any
          indirect, incidental, special, consequential, or punitive damages, including loss of data,
          profits, or business opportunities.
        </Section>

        <Section title="12. Changes to Terms">
          We may modify these Terms at any time. Continued use of CallChat after changes constitutes
          acceptance of the modified Terms. We will make reasonable efforts to notify users of significant
          changes.
        </Section>

        <Section title="13. Governing Law">
          These Terms are governed by and construed in accordance with the laws of India. Any disputes
          arising from these Terms shall be resolved in the courts of competent jurisdiction in India.
        </Section>

        <Section title="14. Contact">
          For questions about these Terms, contact us at:
          <p className="mt-2 text-[#00A884] font-medium">support@callchat.online</p>
        </Section>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h2 className="text-[16px] font-semibold text-[#111B21] mb-2">{title}</h2>
    <div className="text-[14px] leading-relaxed text-[#3B4A54]">{children}</div>
  </div>
);

const BulletList = ({ items }) => (
  <ul className="mt-2 space-y-1.5 pl-4">
    {items.map((item, i) => (
      <li key={i} className="text-[14px] text-[#3B4A54] leading-relaxed list-disc">{item}</li>
    ))}
  </ul>
);

export default TermsPage;
